/**
 * "Priya joined" / "Priya left", as the user's notification settings ask.
 *
 * The Settings page has offered "Participant join alerts", "Participant leave
 * alerts" and "Participant sound" for a long time, and the meeting never read
 * them: nothing in a call announced anyone, whatever was switched on.
 *
 * Gates, read when the alert fires so a toggle applies at once:
 *  - the in-meeting "Notifications" switch (master for every in-call toast) and
 *    "Notification sounds" (master for every in-call sound);
 *  - the account settings: join / leave alerts, participant sound, do-not-
 *    disturb and mute-all (`shouldShowParticipantAlert`, `shouldPlaySound`).
 *
 * A screen-share-only session is announced in its own words ("{name} joined
 * for screen share"), and hosts and co-hosts are always told one joined —
 * see services/participant-notices, which holds every rule.
 *
 * Only `presence_diff` announces anyone. The `presence_state` snapshot on
 * (re)joining lists people who were already there, and is taken silently.
 *
 * A reconnect is not a leave and a join. Presence reports one as both, so a
 * leave only marks someone away until the server's `media:peer_left` confirms
 * they are gone — see services/presence-departures, which holds that rule.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import {
  playNotificationSound,
  settingsService,
  shouldPlaySound,
  shouldShowParticipantAlert,
} from '@/features/settings'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { meetingAlert } from '../services/meeting-alert'
import { useMeetingPreferencesStore } from '../store/meeting-preferences.store'
import {
  applyPresenceDiff,
  applyPresenceState,
  EMPTY_ROSTER,
  type PresenceDiff,
  type PresenceEntry,
  type PresenceRoster,
} from '../services/presence-roster'
import { participantNotices, rosterChanges, type RosterChange } from '../services/participant-notices'
import {
  FALLBACK_LEAVE_MS,
  NO_DEPARTURES,
  onLeaveFallback,
  onPeerLeft,
  onPresenceJoined,
  onPresenceLeft,
  onPresenceSnapshot,
  type DepartureState,
} from '../services/presence-departures'

export function useParticipantAlerts(
  subscribe: ((event: string, handler: PhoenixEventHandler) => () => void) | null,
  selfId: string | null,
  /** Host or co-host right now: always told a screen-share session joined. */
  isModerator = false,
) {
  const t = useTranslation()
  const tRef = useRef(t)
  tRef.current = t
  const isModeratorRef = useRef(isModerator)
  isModeratorRef.current = isModerator

  useEffect(() => {
    if (!subscribe) return
    let roster: PresenceRoster = EMPTY_ROSTER
    let departures: DepartureState = NO_DEPARTURES
    // One per person away: announces them as gone if the server never says.
    const fallbacks = new Map<string, ReturnType<typeof setTimeout>>()
    const cancelFallback = (id: string) => {
      const timer = fallbacks.get(id)
      if (timer) clearTimeout(timer)
      fallbacks.delete(id)
    }

    const announce = (kind: 'join' | 'leave', people: RosterChange[]) => {
      if (people.length === 0) return
      const prefs = settingsService.get().notifications
      const inCall = useMeetingPreferencesStore.getState()
      const tr = tRef.current
      const notices = participantNotices(
        kind,
        people,
        {
          rosterSize: roster.size,
          inMeetingNotifications: inCall.inMeetingNotifications,
          accountAllows: shouldShowParticipantAlert(prefs, kind),
          viewerIsModerator: isModeratorRef.current,
        },
        tr('meetingScreen.participantFallbackName') || 'A participant',
      )
      if (notices.length === 0) return
      for (const notice of notices) {
        meetingAlert(notice.parts.map((part) => tr(part.key, part.vars)).join(', '), { personal: notice.personal })
      }
      if (inCall.inMeetingNotificationSounds && shouldPlaySound(prefs, 'participant')) playNotificationSound('participant')
    }

    const offState = subscribe('presence_state', (payload) => {
      roster = applyPresenceState(roster, payload as Record<string, PresenceEntry>)
      // Our own (re)join: anyone listed who was away is back.
      const snapshot = onPresenceSnapshot(departures, new Set(roster.keys()))
      departures = { away: snapshot.away, departed: snapshot.departed }
      for (const id of snapshot.back) cancelFallback(id)
    })

    const offDiff = subscribe('presence_diff', (payload) => {
      const prevRoster = roster
      const next = applyPresenceDiff(roster, payload as PresenceDiff)
      const { joined, left } = rosterChanges(roster, next, selfId)
      roster = next

      // Coming back while away is a reconnect: not announced either way.
      // Who was here before this update, by name: an old connection of the
      // same person still open (presence-departures).
      const alreadyHere = rosterChanges(EMPTY_ROSTER, prevRoster, selfId).joined
      const arrived = onPresenceJoined(departures, joined, alreadyHere)
      departures = arrived.state
      for (const p of joined) cancelFallback(p.id)
      for (const id of arrived.replaced) cancelFallback(id)
      announce('join', arrived.announce)

      // Everyone still listed, by name — a leave by someone whose new
      // connection is already here is their old one closing (presence-departures).
      const stillHere = rosterChanges(EMPTY_ROSTER, roster, selfId).joined
      const gone = onPresenceLeft(departures, left, stillHere)
      departures = gone.state
      announce('leave', gone.announce)
      for (const p of gone.nowAway) {
        fallbacks.set(
          p.id,
          setTimeout(() => {
            fallbacks.delete(p.id)
            const step = onLeaveFallback(departures, p.id)
            departures = step.state
            announce('leave', step.announce)
          }, FALLBACK_LEAVE_MS),
        )
      }
    })

    // The server's confirmation that a session is over: at once for a real
    // leave, after its grace for a dropped connection that never came back.
    const offPeerLeft = subscribe('media:peer_left', (payload) => {
      const peerId = (payload as { peerId?: unknown } | null)?.peerId
      if (typeof peerId !== 'string' || peerId === selfId) return
      const step = onPeerLeft(departures, peerId, roster.has(peerId))
      departures = step.state
      if (step.announce.length > 0) cancelFallback(peerId)
      announce('leave', step.announce)
    })

    return () => {
      offState()
      offDiff()
      offPeerLeft()
      for (const timer of fallbacks.values()) clearTimeout(timer)
    }
  }, [subscribe, selfId])
}
